import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { ArrowLeft, Home } from 'lucide-react';

// Interface para o projeto
interface Projeto {
  id: number;
  name: string;
  type: string;
  region: string;
  status: string;
  description: string;
  image: string;
  detailedDescription?: string;
  startDate?: string;
  endDate?: string;
  carbonCredits?: number | string;
  partners?: string[];
  contactEmail?: string;
  verificationStandard?: string;
  objectives?: string[];
  benefits?: string[];
  challenges?: string[];
  apoio?: string[];
  realizacao?: string[];
}

const ProjetoDetalhe: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [projeto, setProjeto] = useState<Projeto | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  useEffect(() => {
    // Simulação de busca do projeto pelo ID
    const fetchProjeto = () => {
      setLoading(true);
      
      // Mock dos projetos - em um ambiente real, isso viria de uma API
      const projetosMock = [
        {
          id: 10,
          name: "Moeda Social Carbono",
          type: "Financeiro",
          region: "Nacional",
          status: "Em andamento",
          image: "/moeda-social-carbono.jpg",
          description: "Sistema inovador de moeda social baseada em créditos de carbono, permitindo que comunidades locais participem ativamente da economia verde através do incentivo a práticas sustentáveis.",
          detailedDescription: "A Moeda Social Carbono é um projeto alinhado estrategicamente com a Convenção-Quadro das Nações Unidas sobre Mudança do Clima (UNFCCC, 1992), com os Objetivos de Desenvolvimento Sustentável (ODS), com o Acordo de Paris e apresenta grande contribuição para implementação de Políticas Públicas e coorporativas para enfrentamento da emergência das Mudanças Climáticas.\n\nO projeto propõe a realização de inventário de emissões de GEE das unidades residenciais de 40 domicílios da Colônia Z10 e o cumprimento de metas de redução de emissões, leva as unidades a gerarem créditos de carbono, transformados em Moeda Social \"CARBONO\".\n\nEste sistema inovador permite que comunidades locais participem ativamente da economia verde através do incentivo a práticas sustentáveis, promovendo inclusão social e econômica enquanto contribui para a mitigação das mudanças climáticas.",
          startDate: "Janeiro 2025",
          endDate: "Em andamento",
          carbonCredits: "A verificar",
          partners: [
            "Colônia Almirante Gomes Pereira (Z10)",
            "Secretaria Municipal de Meio Ambiente e Clima da Prefeitura do Rio de Janeiro",
            "CEA- Centro de Educação Ambiental"
          ],
          apoio: ["Moove"],
          realizacao: ["Instituto Terrazul", "Banclima"],
          contactEmail: "contato@moedasocialcarbono.org.br",
          verificationStandard: "Sistema de Registro Banclima",
          objectives: [
            "Realizar inventário de emissões de GEE em 40 domicílios da Colônia Z10",
            "Implementar medidas de redução de emissões nas residências participantes",
            "Transformar a redução de emissões em créditos de carbono monetizáveis",
            "Criar um sistema de Moeda Social local baseada em práticas sustentáveis",
            "Contribuir para implementação de políticas públicas de enfrentamento às mudanças climáticas"
          ],
          benefits: [
            "Redução de emissões de gases de efeito estufa e mitigação climática",
            "Geração de renda para famílias de baixa renda através de práticas sustentáveis",
            "Incentivo à reciclagem e gestão adequada de resíduos",
            "Educação ambiental e conscientização sobre mudanças climáticas",
            "Alinhamento com acordos internacionais de clima e ODS"
          ],
          challenges: [
            "Manutenção do engajamento dos domicílios participantes",
            "Mensuração precisa da redução de emissões em pequena escala",
            "Validação dos créditos de carbono gerados pelas comunidades",
            "Escalabilidade do modelo para outras comunidades",
            "Criação de rede de parceiros para aceitação da moeda social"
          ]
        },
        {
          id: 11,
          name: "Plante para Neutralizar",
          type: "Conservação",
          region: "Sudeste",
          status: "Em Andamento",
          description: "Iniciativa de preservação do Manguezal do Jequiá, com foco na valorização do carbono azul, sequestro de carbono e geração de renda para comunidades locais.",
          image: "/images/projects/comunidades-empresariais.jpg",
          detailedDescription: "O Plante para Neutralizar é resultado da ampliação do Programa de Desenvolvimento Sustentável – PDS, elaborado em 2020, com foco em gestão de resíduos sólidos para Colônia Almirante Gomes Pereira – Colônia Z10. O projeto dá ênfase a preservação do Manguezal do Jequiá, valorização do carbono azul e sequestro de carbono.\n\nA parceria firmada desde 2020 entre o Instituto Terrazul e a empresa MOOVE na busca de proteger o Manguezal do Jequiá, localizado na APARU da ilha do Governador, as margens da Baía de Guanabara, vem proporcionando a oportunidade de geração de renda para os pescadores da Colônia Z10. Esta aliança trouxe também para perto a Associação de Moradores e a de Pescadores da Colônia e o Centro de Educação Ambiental da Secretaria Municipal de Meio Ambiente e Clima da Prefeitura do Rio de Janeiro.\n\nIniciado em setembro de 2021, durante os cinco primeiros meses de trabalho de campo a equipe foi levada a realizar forte processo de limpeza do Manguezal, buscando preparar as áreas e suas subáreas de intervenção para a realização do plantio futuro.",
          startDate: "Setembro 2021",
          endDate: "Em andamento",
          carbonCredits: "A verificar",
          verificationStandard: "Sistema de Registro Banclima",
          partners: [
            "Colônia Almirante Gomes Pereira (Z10)",
            "Secretaria Municipal de Meio Ambiente e Clima da Prefeitura do Rio de Janeiro",
            "CEA- Centro de Educação Ambiental"
          ],
          apoio: ["Moove"],
          realizacao: ["Instituto Terrazul", "Banclima"],
          contactEmail: "contato@planteparaneutralizar.org.br",
          objectives: [
            "Promover a limpeza e conservação do Manguezal do Jequiá e da foz do rio",
            "Produzir mudas de mangue vermelho, branco e preto para reflorestamento",
            "Recuperar áreas degradadas através do plantio de mudas nativas",
            "Sequestrar carbono e valorizar o carbono azul dos manguezais",
            "Gerar renda para pescadores e comunidades locais da Colônia Z10"
          ],
          benefits: [
            "Redução da poluição no manguezal e na Baía de Guanabara",
            "Sequestro de carbono através da restauração do ecossistema de mangue",
            "Proteção da biodiversidade marinha e costeira",
            "Geração de renda para pescadores locais",
            "Educação ambiental e conscientização sobre a importância dos manguezais"
          ],
          challenges: [
            "Acesso às áreas de difícil alcance do manguezal",
            "Poluição constante trazida pelas marés e pelo rio",
            "Engajamento contínuo da comunidade local",
            "Monitoramento do desenvolvimento das mudas plantadas",
            "Quantificação dos créditos de carbono do 'carbono azul'"
          ]
        },
        {
          id: 12,
          name: "Comunidades Empresariais pelo Clima",
          type: "Educação",
          region: "Nacional",
          status: "Em andamento",
          description: "Iniciativa que mobiliza e articula comunidades empresariais para implementar ações concretas de mitigação e adaptação às mudanças climáticas, promovendo práticas sustentáveis no ambiente corporativo brasileiro.",
          image: "/images/projects/comunidades-empresariais.jpg",
          detailedDescription: "O projeto Comunidades Empresariais pelo Clima é uma iniciativa inovadora que visa construir uma rede de empresas comprometidas com a redução de emissões de gases de efeito estufa e implementação de práticas sustentáveis em seus negócios.\n\nAtravés de um programa estruturado, as empresas participantes recebem orientação para realizar inventários de suas emissões, definir metas de redução baseadas na ciência, e implementar planos de ação climática personalizados para suas operações. O trabalho é feito de forma colaborativa, permitindo que empresas de diferentes setores e portes compartilhem experiências e boas práticas.\n\nO projeto promove workshops, eventos de networking, e uma plataforma digital que facilita o acompanhamento das ações e resultados de cada empresa participante. Além disso, promove a integração entre as empresas e órgãos governamentais para alinhamento com políticas públicas de sustentabilidade e resiliência climática.",
          startDate: "Março 2023",
          endDate: "Em andamento",
          carbonCredits: "A verificar",
          verificationStandard: "Sistema de Registro Banclima",
          partners: [
            "Federação das Indústrias",
            "Associação Comercial",
            "Conselho Empresarial para o Desenvolvimento Sustentável",
            "Universidades corporativas"
          ],
          apoio: ["Ministério do Meio Ambiente e Mudança do Clima"],
          realizacao: ["Instituto Terrazul", "Banclima"],
          contactEmail: "contato@comunidadesempresariaispeloclima.org.br",
          objectives: [
            "Mobilizar o setor empresarial para ação climática concreta",
            "Capacitar profissionais para gestão de emissões de carbono",
            "Fomentar a adoção de modelos de negócio de baixo carbono",
            "Promover a troca de experiências entre empresas sobre práticas sustentáveis",
            "Conectar iniciativas empresariais com políticas públicas de clima"
          ],
          benefits: [
            "Redução de emissões de gases de efeito estufa no setor empresarial",
            "Economia de recursos através de eficiência energética e gestão de resíduos",
            "Desenvolvimento de produtos e serviços ecoeficientes",
            "Melhoria da reputação corporativa e acesso a mercados exigentes",
            "Preparação para regulamentações climáticas futuras"
          ],
          challenges: [
            "Engajamento de pequenas e médias empresas com recursos limitados",
            "Integração de ações climáticas ao core business das empresas",
            "Mensuração precisa e comparável de resultados entre diferentes setores",
            "Manutenção do compromisso de longo prazo das empresas participantes",
            "Escalonamento das iniciativas para alcançar impacto significativo"
          ]
        },
        {
          id: 13,
          name: "Secretários pelo Clima",
          type: "Educação",
          region: "Nacional",
          status: "Em andamento",
          description: "Programa que capacita e conecta secretários municipais e estaduais para fortalecer políticas públicas e ações governamentais voltadas para a resiliência climática.",
          image: "/images/SECRETÁRIOS PELO CLIMA.png",
          detailedDescription: "O Projeto Secretários pelo Clima visa envolver adolescentes e jovens com a meta de manter o aumento da temperatura média global dentro do limite de 1,5°C.\n\nLimitar o aquecimento global requer uma redução substancial do desmatamento e da perda de mangues ao longo da próxima década, bem como uma interrupção efetiva da degradação das turfeiras (turfa é um material de origem vegetal, parcialmente decomposto, encontrado em camadas, geralmente em regiões pantanosas e também sob montanhas, fornecem serviços vitais, como o controle do abastecimento de água e a prevenção de enchentes e secas, e provêm alimento e combustível a muitas pessoas).\n\nEntre as consequências do aquecimento global estão o degelo, o aumento do nível dos oceanos, a desertificação, a alteração do regime das chuvas, inundações e a redução da biodiversidade.\n\nOs Secretário pelo Clima após receberem capacitação na sede do Instituto Terrazul, são certificados como responsáveis pela sensibilização de outros alunos de sua unidade escolar quanto às mudanças climáticas, pela elaboração do inventário de gases de efeito estufa da escola e pela realização do Plano de Ação Climática.",
          startDate: "Janeiro 2024",
          endDate: "Em andamento",
          carbonCredits: "A verificar",
          verificationStandard: "Sistema de Registro Banclima",
          partners: [
            "Secretaria Municipal de Educação",
            "Escolas Municipais e Estaduais",
            "Universidade Federal do Rio de Janeiro"
          ],
          apoio: ["Prefeitura Municipal do Rio de Janeiro"],
          realizacao: ["Instituto Terrazul", "Banclima"],
          contactEmail: "contato@secretariospeloclima.org.br",
          objectives: [
            "Capacitar adolescentes e jovens como agentes multiplicadores de conscientização sobre mudanças climáticas",
            "Elaborar inventários de gases de efeito estufa nas escolas participantes",
            "Implementar Planos de Ação Climática em unidades escolares",
            "Reduzir a pegada de carbono das instituições educacionais",
            "Contribuir para a meta global de limitar o aumento da temperatura a 1,5°C"
          ],
          benefits: [
            "Formação de lideranças jovens em temas ambientais e climáticos",
            "Sensibilização da comunidade escolar sobre mudanças climáticas",
            "Redução das emissões de gases de efeito estufa nas escolas",
            "Promoção de práticas sustentáveis nas unidades escolares",
            "Incentivo à implementação de políticas públicas para mitigação climática"
          ],
          challenges: [
            "Engajamento contínuo dos jovens no programa",
            "Implementação prática dos Planos de Ação Climática",
            "Medição e verificação das reduções de emissões nas escolas",
            "Expansão do programa para mais unidades escolares",
            "Integração das iniciativas com políticas públicas existentes"
          ]
        },
        {
          id: 1,
          name: "Reflorestamento Mata Atlântica",
          type: "Reflorestamento",
          region: "Sudeste",
          status: "Em implementação",
          description: "Projeto de reflorestamento de áreas degradadas da Mata Atlântica, promovendo a recuperação da biodiversidade e absorção de carbono.",
          image: "/lovable-uploads/projeto-reflorestamento.jpg",
          detailedDescription: "O projeto de Reflorestamento da Mata Atlântica foca na recuperação de áreas degradadas em um dos biomas mais ameaçados do Brasil. Através do plantio estratégico de espécies nativas em diferentes estágios sucessionais, o projeto busca acelerar o processo de regeneração florestal, criar corredores ecológicos para a fauna local e estabelecer sumidouros de carbono de longo prazo.",
          startDate: "Março 2021",
          endDate: "Março 2031",
          carbonCredits: 12000,
          partners: ["SOS Mata Atlântica", "Instituto Terra", "Universidade de São Paulo"],
          contactEmail: "contato@reflorestamatlantica.org.br",
          verificationStandard: "Verified Carbon Standard (VCS)",
          objectives: [
            "Reflorestar 2.000 hectares de Mata Atlântica",
            "Criar corredores ecológicos entre fragmentos florestais existentes",
            "Sequestrar 12.000 toneladas de CO2 equivalente",
            "Proteger nascentes e cursos d'água"
          ],
          benefits: [
            "Sequestro de carbono",
            "Conservação da biodiversidade",
            "Proteção de recursos hídricos",
            "Educação ambiental para comunidades locais"
          ],
          challenges: [
            "Combate a espécies invasoras",
            "Prevenção de incêndios florestais",
            "Manutenção de longo prazo das áreas reflorestadas",
            "Engajamento de proprietários rurais"
          ]
        },
        {
          id: 2,
          name: "Energia Solar Comunitária",
          type: "Energia Renovável",
          region: "Nordeste",
          status: "Planejamento",
          description: "Instalação de painéis solares em comunidades de baixa renda, promovendo acesso à energia limpa e redução de emissões.",
          image: "/lovable-uploads/projeto-energia-solar.jpg",
          detailedDescription: "O projeto Energia Solar Comunitária visa democratizar o acesso à energia renovável através da instalação de sistemas fotovoltaicos em comunidades de baixa renda no Nordeste brasileiro. Além de reduzir as emissões de gases de efeito estufa, o projeto promove a inclusão energética, reduz custos com eletricidade para famílias vulneráveis e cria oportunidades de capacitação técnica e geração de renda local.",
          startDate: "Agosto 2023",
          endDate: "Agosto 2028",
          carbonCredits: 8500,
          partners: ["Instituto Solar Nordeste", "Cooperativa de Eletrificação Rural", "Universidade Federal do Ceará"],
          contactEmail: "contato@solarcomunitaria.org.br",
          verificationStandard: "Clean Development Mechanism (CDM)",
          objectives: [
            "Instalar 500 kW de energia solar fotovoltaica",
            "Beneficiar 1.000 famílias em comunidades de baixa renda",
            "Reduzir 8.500 toneladas de CO2 equivalente",
            "Criar um modelo replicável de energia comunitária"
          ],
          benefits: [
            "Acesso à energia limpa e renovável",
            "Redução na conta de energia para famílias de baixa renda",
            "Capacitação técnica e geração de empregos locais",
            "Redução de emissões de gases de efeito estufa"
          ],
          challenges: [
            "Financiamento inicial para instalação dos sistemas",
            "Manutenção de longo prazo dos equipamentos",
            "Adaptação da rede elétrica existente",
            "Coordenação entre múltiplos parceiros e comunidades"
          ]
        },
        {
          id: 3,
          name: "Gestão de Resíduos Urbanos",
          type: "Manejo de Resíduos",
          region: "Sul",
          status: "Finalizado",
          description: "Implementação de sistema integrado de coleta seletiva e tratamento de resíduos urbanos, evitando emissões de metano.",
          image: "/lovable-uploads/projeto-residuos.jpg",
          detailedDescription: "O projeto de Gestão de Resíduos Urbanos implementou um sistema completo de coleta seletiva, reciclagem e compostagem em municípios da região Sul do Brasil. Com foco na redução das emissões de metano provenientes de aterros sanitários, o projeto incluiu a construção de centrais de triagem, biodigestores para resíduos orgânicos e programas de educação ambiental para a população local.",
          startDate: "Janeiro 2018",
          endDate: "Dezembro 2022",
          carbonCredits: 18000,
          partners: ["Associação de Catadores", "Prefeituras Municipais", "Empresa de Saneamento Ambiental"],
          contactEmail: "contato@residuosurbanos.org.br",
          verificationStandard: "Gold Standard",
          objectives: [
            "Reduzir em 80% o volume de resíduos enviados para aterros",
            "Implementar coleta seletiva em 5 municípios",
            "Evitar a emissão de 18.000 toneladas de CO2 equivalente",
            "Criar empregos formais para catadores de materiais recicláveis"
          ],
          benefits: [
            "Redução de emissões de metano",
            "Aumento da vida útil dos aterros sanitários",
            "Geração de empregos para catadores",
            "Produção de composto orgânico para agricultura urbana",
            "Educação ambiental para a população"
          ],
          challenges: [
            "Mudança de hábitos da população",
            "Integração de catadores informais ao sistema formal",
            "Logística de coleta em áreas de difícil acesso",
            "Sustentabilidade financeira do modelo"
          ]
        },
        {
          id: 14,
          name: "Rede Nacional Fóruns de Juventudes Água e Clima",
          type: "Educação",
          region: "Nacional",
          status: "Em andamento",
          description: "Rede que mobiliza jovens de todo o Brasil para discussão, formação e implementação de ações relacionadas à gestão hídrica e enfrentamento das mudanças climáticas.",
          image: "/lovable-uploads/7a72b832-cca8-4f76-b696-877484eef2cd.png",
          detailedDescription: "REDE NACIONAL\nFÓRUNS DE JUVENTUDES ÁGUA E CLIMA\nSecretaria Executiva Instituto Terrazul redefjac@gmail.com\n\nAos Comitês de Bacias Hidrográficas, instituições públicas e privadas\n\nPrezados(as),\n\nÉ com grande prazer que a Secretaria Executiva (Instituto Terrazul) da REDE NACIONAL FÓRUNS DE JUVENTUDES ÁGUA E CLIMA - REDE NACIONAL FJAC, formada inicialmente como REDE NACIONAL FÓRUNS DE JUVENTUDES EM RECURSOS HÍDRICOS - REDE NACIONAL FJRH, no âmbito do Encontro Setorial da Juventude, em 22 de outubro de 2019, no XXI Encontro Nacional de Comitês de Bacias Hidrográficas - XXI ENCOB, em Foz de Iguaçu, apresenta a proposta de retomada da REDE e sua ampliação para Água e Clima.\n\nA REDE NACIONAL FJAC é resultado da articulação de entidades comprometidas com a formação, o envolvimento e a participação de jovens nos debates da gestão das águas por bacias hidrográficas e o enfrentamento das consequências das mudanças climáticas.\n\nA Rede surge a partir de iniciativas no Estado do Rio de Janeiro de formação de Fóruns de Juventudes em Recursos Hídricos e vem ampliando sua articulação para além fronteiras, com vistas a fomentar a criação de espaços de referências para jovens no tocante à Mobilização, Capacitação e Disseminação da Gestão de Recursos Hídricos no interior dos Comitês de Bacias, de instituições de ensino, comunidades e poder público, fortalecendo os conceitos do uso racional da água, da gestão participativa, do fortalecimento do Sistema de Gestão de Recursos Hídricos e agora também, de Mudanças Climáticas.\n\nNo Encontro Setorial, mediado pelo Instituto Terrazul, entidade da Sociedade Civil e que a época exercia a função de presidência do Comitê da Região Hidrográfica da Baía de Guanabara (CBH BG), depois de ser apresentado as experiências de criação de Fóruns de Juventudes em bacias hidrográficas e outras iniciativas de coletivos do país, a proposta de criação da REDE foi aprovada e a Carta do Encontro Setorial da Juventude protocolada na Secretaria do Fórum Nacional, manifestando a decisão de criação da REDE NACIONAL FJRH, atualmente REDE NACIONAL FJAC.\n\nA REDE é uma organização que nasce no contexto do terceiro setor, com missão e objetivos de uma organização sem fins lucrativos, de forma descentralizada e sem uma estrutura de comando vertical.\n\nAssim, a Instituição Mediadora do Encontro Setorial assume apenas o papel de Secretaria Executiva da REDE, convidando instituições integrantes do sistema de gestão de recursos hídricos, instituições de ensino, ong`s, instâncias do poder público municipal, estadual e federal e empresas que promovam ou que desejam promover a participação de jovens na gestão das águas e no enfrentamento das consequências das mudanças climáticas, a assinarem o termo Manifesto de Adesão à REDE NACIONAL FJAC, na qualidade de signatária, articuladora local da REDE e promotora de Fóruns das Juventudes em seu território de atuação.\n\nSecretaria Executiva\nInstituto Terrazul",
          startDate: "Agosto 2023",
          endDate: "Em andamento",
          carbonCredits: "A verificar",
          verificationStandard: "Sistema de Registro Banclima",
          partners: [
            "Agência Nacional de Águas",
            "Coletivos de Juventudes",
            "Universidades Federais",
            "Comitês de Bacias Hidrográficas"
          ],
          apoio: ["Programa das Nações Unidas para o Desenvolvimento (PNUD)"],
          realizacao: ["Instituto Terrazul", "Banclima"],
          contactEmail: "redefjac@gmail.com",
          objectives: [
            "Formar jovens lideranças em gestão hídrica e ação climática",
            "Estabelecer uma rede nacional de articulação entre coletivos juvenis",
            "Promover a participação de jovens nos espaços de governança ambiental",
            "Implementar projetos locais de gestão da água e mitigação climática",
            "Influenciar políticas públicas através da mobilização juvenil"
          ],
          benefits: [
            "Fortalecimento de lideranças juvenis em temas ambientais",
            "Criação de uma rede de colaboração e troca de conhecimentos",
            "Implementação de soluções inovadoras adaptadas a diferentes realidades locais",
            "Maior representatividade juvenil em espaços decisórios",
            "Sensibilização de comunidades sobre a importância da gestão hídrica e climática"
          ],
          challenges: [
            "Manutenção do engajamento de jovens em diferentes regiões do país",
            "Garantia de recursos para implementação de projetos locais",
            "Superação de barreiras de acesso digital em regiões remotas",
            "Integração de conhecimentos técnicos e saberes tradicionais",
            "Articulação com políticas públicas existentes"
          ]
        },
        {
          id: 15,
          name: "Programa de Tecnologia de Baixo Carbono para Micro e Pequenas Empresas",
          type: "Tecnologia",
          region: "Nacional",
          status: "Em andamento",
          description: "Iniciativa que desenvolve e disponibiliza soluções tecnológicas de baixo carbono acessíveis para micro e pequenas empresas.",
          image: "/images/projects/tecnologia-baixo-carbono.jpg",
          detailedDescription: "O Programa de Tecnologia de Baixo Carbono para Micro e Pequenas Empresas foi criado para democratizar o acesso a soluções tecnológicas que reduzam a pegada de carbono dos pequenos negócios brasileiros. Reconhecendo que as micro e pequenas empresas representam mais de 90% do tecido empresarial do país, o programa busca acelerar a transição para uma economia de baixo carbono a partir da base da pirâmide econômica.\n\nO programa atua em três frentes principais: desenvolvimento e adaptação de tecnologias acessíveis e de baixo custo; capacitação técnica de empreendedores e suas equipes; e facilitação do acesso a crédito para implementação das soluções. São oferecidas consultorias personalizadas, workshops temáticos e uma plataforma digital com ferramentas de autodiagnóstico e recomendações específicas para cada setor.\n\nAs soluções abrangem eficiência energética, gestão de resíduos, logística sustentável e processos produtivos com menor intensidade de carbono, sempre considerando a realidade financeira e operacional dos pequenos negócios.",
          startDate: "Fevereiro 2023",
          endDate: "Em andamento",
          carbonCredits: "A verificar",
          verificationStandard: "Sistema de Registro Banclima",
          partners: [
            "SEBRAE",
            "Associações Comerciais",
            "Centros de Inovação",
            "Universidades e Institutos de Pesquisa"
          ],
          apoio: ["Banco Nacional de Desenvolvimento Econômico e Social (BNDES)"],
          realizacao: ["Instituto Terrazul", "Banclima"],
          contactEmail: "contato@tecnologiabaixocarbono.org.br",
          objectives: [
            "Desenvolver e adaptar tecnologias de baixo carbono acessíveis para pequenos negócios",
            "Capacitar empreendedores em gestão de carbono e eficiência de recursos",
            "Facilitar o acesso a financiamento para implementação de soluções sustentáveis",
            "Reduzir a pegada de carbono do setor de micro e pequenas empresas",
            "Criar um ecossistema de fornecedores e prestadores de serviços de tecnologias verdes"
          ],
          benefits: [
            "Redução de custos operacionais através da eficiência energética e de recursos",
            "Acesso a tecnologias verdes anteriormente disponíveis apenas para grandes empresas",
            "Melhoria da competitividade e diferenciação no mercado",
            "Preparação para futuros requisitos regulatórios e de mercado",
            "Contribuição para a mitigação das mudanças climáticas"
          ],
          challenges: [
            "Limitações financeiras das micro e pequenas empresas para investimentos iniciais",
            "Adaptação das tecnologias às diferentes realidades setoriais",
            "Resistência cultural à mudança de processos estabelecidos",
            "Mensuração de resultados em negócios com baixa digitalização",
            "Escalabilidade das soluções para atender um setor numeroso e disperso"
          ]
        },
        {
          id: 16,
          name: "Programa de Habitação Sustentável",
          type: "Infraestrutura",
          region: "Nacional",
          status: "Em andamento",
          description: "Programa que promove a construção e reforma de habitações com critérios de sustentabilidade, utilizando materiais ecológicos e tecnologias eficientes.",
          image: "/images/projects/habitacao-sustentavel.jpg",
          detailedDescription: "O Programa de Habitação Sustentável é uma iniciativa que busca transformar o setor da construção civil residencial no Brasil, promovendo a adoção de técnicas, materiais e tecnologias de baixo impacto ambiental. O programa trabalha tanto na construção de novas moradias quanto na readequação de habitações existentes, com foco em populações de baixa e média renda.\n\nAdotando os princípios da bioconstrução e eficiência energética, o programa desenvolve e implementa soluções como sistemas de captação e reuso de água, energia solar fotovoltaica e térmica, materiais construtivos de baixa pegada de carbono, e técnicas de conforto térmico passivo. Além do aspecto ambiental, o programa também considera a viabilidade econômica das soluções e seu impacto social.\n\nUm componente central do programa é a capacitação da mão de obra local e o envolvimento das comunidades no processo construtivo, gerando conhecimento, emprego e renda. O programa também trabalha em parceria com órgãos públicos para influenciar políticas habitacionais e códigos de obras no sentido de incentivar construções mais sustentáveis.",
          startDate: "Maio 2023",
          endDate: "Em andamento",
          carbonCredits: "A verificar",
          verificationStandard: "Sistema de Registro Banclima",
          partners: [
            "Caixa Econômica Federal",
            "Associações de Arquitetura e Engenharia Sustentável",
            "Secretarias Municipais de Habitação",
            "Cooperativas Habitacionais"
          ],
          apoio: ["Ministério das Cidades"],
          realizacao: ["Instituto Terrazul", "Banclima"],
          contactEmail: "contato@habitacaosustentavel.org.br",
          objectives: [
            "Implementar soluções de eficiência energética e hídrica em habitações de interesse social",
            "Desenvolver e difundir técnicas construtivas de baixa pegada de carbono",
            "Capacitar profissionais da construção civil em práticas sustentáveis",
            "Reduzir o impacto ambiental do setor habitacional brasileiro",
            "Contribuir para a economia no orçamento familiar através da redução de contas de energia e água"
          ],
          benefits: [
            "Redução de emissões de gases de efeito estufa no setor da construção civil",
            "Economia de recursos naturais como água e energia",
            "Melhoria da qualidade de vida e conforto térmico dos moradores",
            "Geração de empregos verdes na construção civil",
            "Desenvolvimento de cadeias produtivas locais de materiais sustentáveis"
          ],
          challenges: [
            "Custos iniciais mais elevados de algumas tecnologias sustentáveis",
            "Resistência cultural a novos métodos construtivos",
            "Necessidade de adaptação às diferentes realidades climáticas do país",
            "Falta de normatização para certos materiais e técnicas alternativos",
            "Integração das soluções sustentáveis com programas habitacionais existentes"
          ]
        }
      ];
      
      // Encontrar o projeto pelo ID
      const projetoEncontrado = projetosMock.find(
        proj => proj.id === parseInt(id || '0')
      );
      
      if (projetoEncontrado) {
        setProjeto(projetoEncontrado);
      } else {
        console.error('Projeto não encontrado');
      }
      
      setLoading(false);
    };
    
    fetchProjeto();
  }, [id]);

  // Função para voltar para a página anterior
  const voltar = () => {
    navigate(-1);
  };

  // Função para navegar para a página inicial
  const irParaHome = () => {
    navigate('/dashboard');
  };

  // Função para formatar o status com a cor apropriada
  const getStatusClass = (status: string) => {
    switch(status) {
      case 'Em implementação':
        return 'bg-blue-100 text-blue-800';
      case 'Finalizado':
        return 'bg-green-100 text-green-800';
      case 'Planejamento':
        return 'bg-yellow-100 text-yellow-800';
      case 'Crédito para Venda':
        return 'bg-purple-100 text-purple-800';
      case 'Concluído':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      
      {/* Botões de navegação */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex space-x-2">
            <button
              onClick={voltar}
              className="flex items-center text-gray-600 hover:text-[#0e7cb4]"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
              Voltar
            </button>
          </div>
          <button
            onClick={irParaHome}
            className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white px-4 py-2 rounded flex items-center justify-center"
          >
            <Home className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Conteúdo principal */}
      <div className="flex-grow p-6 bg-gray-50">
        <div className="container mx-auto">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0e7cb4]"></div>
            </div>
          ) : projeto ? (
            <div>
              {/* Banner do projeto */}
              <div className="relative w-full h-64 bg-gradient-to-b from-[#0e7cb4] to-[#1397d5] rounded-t-lg md:rounded-t-xl">
                {/* Detalhes do projeto no banner */}
                <div className="absolute bottom-0 left-0 w-full p-4 md:p-6 text-white">
                  <h1 className="text-2xl md:text-3xl font-bold mb-2">{projeto.name}</h1>
                  
                  {/* Se o projeto for 10, 11, 12, 13, 14, 15 ou 16, mostra apenas o status */}
                  {(projeto.id === 10 || projeto.id === 11 || projeto.id === 12 || projeto.id === 13 || projeto.id === 14 || projeto.id === 15 || projeto.id === 16) ? (
                    <span className="inline-block bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {projeto.status}
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-block bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {projeto.type}
                      </span>
                      <span className="inline-block bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {projeto.region}
                      </span>
                      <span className="inline-block bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {projeto.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Grid de informações */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Coluna principal */}
                <div className="md:col-span-2 space-y-6">
                  {/* Descrição */}
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Sobre o Projeto</h2>
                    <p className="text-gray-700 mb-4">{projeto.detailedDescription || projeto.description}</p>
                    
                    {/* Vídeo Instagram do projeto - apenas para o projeto Moeda Social Carbono */}
                    {projeto.id === 10 && (
                      <div className="my-6">
                        <h3 className="text-lg font-medium mb-2 text-[#0e7cb4]">Assista ao vídeo do projeto</h3>
                        <p className="text-gray-600 mb-2 text-sm">Você pode assistir ao vídeo sem sair desta página ou em tela cheia.</p>
                        <div className="w-full rounded-lg overflow-hidden shadow mb-2 relative" style={{ position: 'relative' }}>
                          <iframe
                            src="https://www.instagram.com/p/DJZ_C2JvafJ/embed"
                            title="Vídeo do Projeto Moeda Social Carbono"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            className="w-full min-h-[400px]"
                            style={{ border: 0 }}
                          ></iframe>
                          {/* Overlay para ocultar o botão "Ver perfil" do Instagram */}
                          <div 
                            className="absolute bottom-0 right-0 bg-white pointer-events-none"
                            style={{ 
                              width: '140px',
                              height: '50px',
                              zIndex: 9999,
                              background: 'white',
                              borderRadius: '0 0 8px 0',
                              boxShadow: '0 0 10px rgba(0,0,0,0.1)'
                            }}
                          ></div>
                        </div>
                        <button
                          className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white px-4 py-2 rounded font-medium"
                          onClick={() => setIsVideoModalOpen(true)}
                        >
                          Assistir em tela cheia
                        </button>
                        {/* Modal do vídeo */}
                        {isVideoModalOpen && (
                          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
                            <div className="relative w-full max-w-3xl mx-4">
                              <button
                                className="absolute top-2 right-2 bg-white rounded-full p-2 shadow hover:bg-gray-200 z-10"
                                onClick={() => setIsVideoModalOpen(false)}
                                aria-label="Fechar vídeo"
                              >
                                <span className="text-xl font-bold text-gray-700">&times;</span>
                              </button>
                              <div className="aspect-w-16 aspect-h-9 w-full rounded-lg overflow-hidden shadow-lg bg-black">
                                <iframe
                                  src="https://www.instagram.com/p/DJZ_C2JvafJ/embed"
                                  title="Vídeo do Projeto Moeda Social Carbono (Tela Cheia)"
                                  allow="autoplay; encrypted-media"
                                  allowFullScreen
                                  className="w-full h-[60vh] min-h-[300px]"
                                  style={{ border: 0 }}
                                ></iframe>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Resultados do projeto - apenas para o projeto Plante para Neutralizar */}
                    {projeto.id === 11 && (
                      <div className="mt-6 bg-green-50 p-4 rounded-lg border border-green-200">
                        <h3 className="text-lg font-medium mb-3 text-green-800">RESULTADOS ALCANÇADOS ATÉ O MOMENTO</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Limpeza */}
                          <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
                            <h4 className="font-semibold text-green-700 mb-2">AÇÕES DE LIMPEZA DO MANGUEZAL E DA FOZ DO RIO JEQUIÁ</h4>
                            <div className="flex justify-between items-center mt-3">
                              <span className="text-gray-600 text-sm">Resíduos coletados:</span>
                              <span className="text-2xl font-bold text-green-800">3.056,26 Kg</span>
                            </div>
                          </div>
                          
                          {/* Produção de mudas */}
                          <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
                            <h4 className="font-semibold text-green-700 mb-2">PRODUÇÃO DE MUDAS DE MANGUE</h4>
                            <p className="text-xs text-gray-500">Variedades: Mangue Vermelho, Branco e Preto</p>
                            <div className="flex justify-between items-center mt-3">
                              <span className="text-gray-600 text-sm">Mudas produzidas:</span>
                              <span className="text-2xl font-bold text-green-800">4.900</span>
                            </div>
                          </div>
                          
                          {/* Plantio */}
                          <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
                            <h4 className="font-semibold text-green-700 mb-2">RECUPERAÇÃO DE ÁREAS DEGRADADAS</h4>
                            <p className="text-xs text-gray-500">Sequestro de carbono e restauração do manguezal</p>
                            <div className="flex justify-between items-center mt-3">
                              <span className="text-gray-600 text-sm">Mudas plantadas:</span>
                              <span className="text-2xl font-bold text-green-800">3.066</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Resultados do projeto - apenas para o projeto Moeda Social Carbono */}
                    {projeto.id === 10 && (
                      <div className="mt-6 bg-[#e6f3f9] p-4 rounded-lg border border-[#a8d5ec]">
                        <h3 className="text-lg font-medium mb-3 text-[#0e7cb4]">RESULTADOS ALCANÇADOS ATÉ O MOMENTO</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Resíduos recicláveis */}
                          <div className="bg-white p-4 rounded-lg shadow-sm border border-[#c5e2f1]">
                            <h4 className="font-semibold text-[#0e7cb4] mb-2">RESÍDUOS RECICLÁVEIS COLETADOS</h4>
                            <div className="flex justify-between items-center mt-3">
                              <span className="text-gray-600 text-sm">Total coletado:</span>
                              <span className="text-lg font-bold text-[#0e7cb4] whitespace-nowrap">1.716,74 Kg</span>
                            </div>
                          </div>
                          
                          {/* Emissões evitadas */}
                          <div className="bg-white p-4 rounded-lg shadow-sm border border-[#c5e2f1]">
                            <h4 className="font-semibold text-[#0e7cb4] mb-2">EMISSÕES EVITADAS</h4>
                            <p className="text-xs text-gray-500">Mês de abril: 2.509,97 KgCO₂e</p>
                            <div className="flex justify-between items-center mt-3">
                              <span className="text-gray-600 text-sm">Total evitado:</span>
                              <span className="text-lg font-bold text-[#0e7cb4] whitespace-nowrap">5.280,80 KgCO₂e</span>
                            </div>
                          </div>
                          
                          {/* Benefícios econômicos */}
                          <div className="bg-white p-4 rounded-lg shadow-sm border border-[#c5e2f1]">
                            <h4 className="font-semibold text-[#0e7cb4] mb-2">BENEFÍCIOS ECONÔMICOS</h4>
                            <p className="text-xs text-gray-500">Geração de renda para 40 domicílios</p>
                            <div className="flex justify-between items-center mt-3">
                              <span className="text-gray-600 text-sm">Total acumulado:</span>
                              <span className="text-lg font-bold text-[#0e7cb4] whitespace-nowrap">R$ 2.740,00</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Objetivos */}
                    {projeto.objectives && projeto.objectives.length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-lg font-medium mb-3 text-gray-800">Objetivos</h3>
                        <ul className="list-disc pl-5 space-y-1 text-gray-700">
                          {projeto.objectives.map((objetivo, index) => (
                            <li key={index}>{objetivo}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Benefícios e Desafios */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      {/* Benefícios */}
                      {projeto.benefits && projeto.benefits.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium mb-3 text-gray-800">Benefícios</h3>
                          <ul className="list-disc pl-5 space-y-1 text-gray-700">
                            {projeto.benefits.map((beneficio, index) => (
                              <li key={index}>{beneficio}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Desafios */}
                      {projeto.challenges && projeto.challenges.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium mb-3 text-gray-800">Desafios</h3>
                          <ul className="list-disc pl-5 space-y-1 text-gray-700">
                            {projeto.challenges.map((desafio, index) => (
                              <li key={index}>{desafio}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Coluna lateral */}
                <div className="space-y-6">
                  {/* Card de informações */}
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Informações</h2>
                    
                    <div className="space-y-4">
                      {/* Período */}
                      {(projeto.startDate || projeto.endDate) && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Período</h3>
                          <p className="text-gray-800">
                            {projeto.startDate && projeto.endDate 
                              ? `${projeto.startDate} - ${projeto.endDate}`
                              : projeto.startDate || projeto.endDate}
                          </p>
                        </div>
                      )}
                      
                      {/* Créditos de Carbono */}
                      {projeto.carbonCredits && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Créditos de Carbono</h3>
                          <p className="text-gray-800">
                            {typeof projeto.carbonCredits === 'number' 
                              ? `${projeto.carbonCredits.toLocaleString()} toneladas de CO₂e` 
                              : projeto.carbonCredits}
                          </p>
                        </div>
                      )}
                      
                      {/* Padrão de Verificação */}
                      {projeto.verificationStandard && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Padrão de Verificação</h3>
                          <p className="text-gray-800">{projeto.verificationStandard}</p>
                        </div>
                      )}
                      
                      {/* Parceiros */}
                      {projeto.partners && projeto.partners.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Parceiros</h3>
                          <ul className="list-disc pl-5 text-gray-800 text-sm">
                            {projeto.partners.map((parceiro, index) => (
                              <li key={index}>{parceiro}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Apoio */}
                      {projeto.apoio && projeto.apoio.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Apoio</h3>
                          <ul className="list-disc pl-5 text-gray-800 text-sm">
                            {projeto.apoio.map((entidade, index) => (
                              <li key={index}>{entidade}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Realização */}
                      {projeto.realizacao && projeto.realizacao.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Realização</h3>
                          <ul className="list-disc pl-5 text-gray-800 text-sm">
                            {projeto.realizacao.map((entidade, index) => (
                              <li key={index}>{entidade}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Contato */}
                      {projeto.contactEmail && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Contato</h3>
                          <p className="text-gray-800">{projeto.contactEmail}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Projeto não encontrado</h2>
              <p className="text-gray-600 mb-6">O projeto que você está procurando não existe ou foi removido.</p>
              <button
                onClick={() => navigate('/all-projects')}
                className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white px-4 py-2 rounded"
              >
                Ver todos os Projetos
              </button>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ProjetoDetalhe; 